import React from 'react';
import { Box, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import type { ThemeColors } from '../../context/ThemeContext';

export interface PillTab {
  value: string;
  label: string;
  /** shorter label used below the sm breakpoint */
  mobileLabel?: string;
}

interface PillTabBarProps {
  tabs: PillTab[];
  value: string;
  onChange: (value: string) => void;
  theme: ThemeColors;
}

/**
 * Compact pill-style tab bar — the finance module's nav pattern
 * (FinanceTabBar), extracted for value-based in-page tabs so modules stay
 * visually consistent. 32px pills, 0.75rem labels, horizontal scroll on
 * mobile with an edge fade.
 */
const PillTabBar: React.FC<PillTabBarProps> = ({ tabs, value, onChange, theme }) => {
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '6px',
        overflowX: 'auto',
        pb: '2px',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        WebkitMaskImage: {
          xs: 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)',
          sm: 'none',
        },
        maskImage: {
          xs: 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)',
          sm: 'none',
        },
      }}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        const label = isMobile && tab.mobileLabel ? tab.mobileLabel : tab.label;
        return (
          <button
            key={tab.value}
            onClick={() => onChange(tab.value)}
            data-testid={`pill-tab-${tab.value}`}
            style={{
              backgroundColor: active ? theme.primary || 'var(--action)' : 'transparent',
              color: active ? '#FFFFFF' : theme.gray?.[500] || '#888',
              fontWeight: active ? 600 : 500,
              border: active ? 'none' : `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
              borderRadius: 20,
              padding: isMobile ? '6px 10px' : '6px 14px',
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              fontFamily: 'inherit',
              lineHeight: 1.4,
              minHeight: 32,
              transition: 'all 0.15s ease',
              flexShrink: 0,
            }}
          >
            {label}
          </button>
        );
      })}
    </Box>
  );
};

export default PillTabBar;
